import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import RichText from "@/lib/richText";
import { useAuth } from "@/context/AuthContext";
import {
    MessageSquare, Send, BookmarkPlus, BookmarkCheck, Trash2, ChevronDown,
    LayoutGrid, CornerDownRight, Users, Dumbbell, Calendar, Star, Lock, Globe, Repeat2, Pencil, Check,
} from "lucide-react";
import { toast } from "sonner";
import ThankButton from "@/components/ThankButton";
import FollowAlertsBell from "@/components/FollowAlertsBell";
import UserLink from "@/components/UserLink";
import TierPortal from "@/components/TierPortal";
import { useHashScroll } from "@/lib/useHashScroll";
import { tierFor } from "@/lib/tiers";
import PostMedia from "@/components/PostMedia";
import PollView from "@/components/PollView";
import PostActions from "@/components/PostActions";
import { ReactionBar } from "@/components/BoardReplies";
import { collapseRanges, ALL_AGES, GRADES } from "@/lib/sports";
import MiniComposer from "@/components/MiniComposer";
import AuthorThankTip from "@/components/AuthorThankTip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TITLES, titleColor, isProUser } from "@/lib/profiles";
import FeedComposer from "@/components/FeedComposer";
import FeedPostCard from "@/components/FeedPostCard";
import { confirmAction } from "@/lib/confirm";

// Title picker shown under the username on the profile page (owner only).
// Any account type may pick a title; colors differ by tier (see titleColor).
function TitleSelect({ value, isPro, onChange }) {
    return (
        <Select value={value || "__none__"} onValueChange={(v) => onChange(v === "__none__" ? "" : v)}>
            <SelectTrigger
                data-testid="profile-title-select"
                className="h-7 w-auto min-w-[7.5rem] gap-2 rounded-none bg-[#0f0f0f] border-[#2a2a2a] text-[11px] uppercase tracking-[0.15em] font-bold"
                style={{ color: titleColor(value, isPro) }}
            >
                <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#2a2a2a] rounded-none">
                <SelectItem value="__none__" className="text-[#a0a0a0]">None</SelectItem>
                {TITLES.map((tt) => (
                    <SelectItem
                        key={tt}
                        value={tt}
                        data-testid={`profile-title-opt-${tt.toLowerCase()}`}
                        style={{ color: titleColor(tt, isPro) }}
                    >
                        {tt}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

const fmt = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
    catch { return ""; }
};

const SECTION_META = {
    wall: { label: "Wall", icon: MessageSquare },
    posts: { label: "Posts", icon: MessageSquare },
    replies: { label: "Replies", icon: CornerDownRight },
    teams: { label: "Teams", icon: Users },
    trainers: { label: "Trainers", icon: Dumbbell },
    events: { label: "Events", icon: Calendar },
    reviews: { label: "Reviews", icon: Star },
};

// Brand colours matching each type's icon elsewhere on the site.
const SECTION_COLOR = {
    wall: "#a855f7",
    posts: "#ff4500",
    replies: "#ff4500",
    teams: "#ff4500",
    trainers: "#2196f3",
    events: "#22c55e",
    reviews: "#fbbf24",
};

const CHIP_STYLE = {
    team: { c: "#ff8a3d", bg: "rgba(255,69,0,0.13)", bd: "rgba(255,69,0,0.4)", icon: Users },
    trainer: { c: "#4aa3f0", bg: "rgba(33,150,243,0.13)", bd: "rgba(33,150,243,0.4)", icon: Dumbbell },
    trainer_listing: { c: "#4aa3f0", bg: "rgba(33,150,243,0.13)", bd: "rgba(33,150,243,0.4)", icon: Dumbbell },
    event: { c: "#39d353", bg: "rgba(34,197,94,0.13)", bd: "rgba(34,197,94,0.4)", icon: Calendar },
    wall: { c: "#c8a2ff", bg: "rgba(168,85,247,0.13)", bd: "rgba(168,85,247,0.4)", icon: MessageSquare },
};
function ContextChip({ chip }) {
    if (!chip || !chip.name) return null;
    const st = CHIP_STYLE[chip.type] || CHIP_STYLE.wall;
    const Icon = st.icon;
    const inner = (
        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.06em] px-2 py-0.5 rounded-full whitespace-nowrap" style={{ color: st.c, background: st.bg, border: `1px solid ${st.bd}` }}>
            <Icon className="h-3 w-3" /> {chip.name}
        </span>
    );
    if (chip.link) return <Link to={chip.link} onClick={(e) => e.stopPropagation()} className="hover:opacity-80 transition-opacity">{inner}</Link>;
    return inner;
}

// ---------------------------------------------------------------------------
// Comments (reused for the Posts tab)
// ---------------------------------------------------------------------------
function Comments({ postId }) {
    const { user } = useAuth();
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try { const { data } = await api.get(`/posts/${postId}/comments`); setComments(data); }
        catch { /* ignore */ } finally { setLoading(false); }
    }, [postId]);
    useEffect(() => { load(); }, [load]);

    const submit = async ({ body: text, gif }) => {
        await api.post(`/posts/${postId}/comments`, { body: text, gif });
        load();
    };
    const remove = async (id) => {
        try { await api.delete(`/comments/${id}`); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
    };

    return (
        <div className="mt-3 border-t border-[#2a2a2a] pt-3 space-y-2">
            {loading ? (
                <div className="text-xs text-[#a0a0a0]">Loading comments…</div>
            ) : comments.length === 0 ? (
                <div className="text-xs text-[#a0a0a0]" data-testid={`no-comments-${postId}`}>No comments yet.</div>
            ) : (
                comments.map((c) => (
                    <div key={c.id} data-testid={`comment-${c.id}`} className="flex items-start gap-2 text-sm">
                        <div className="flex-1 min-w-0">
                            <UserLink username={c.author_username} className="text-[#ff4500] font-semibold" />
                            <span className="text-[10px] text-[#666] ml-2">{new Date(c.created_at).toLocaleString()}</span>
                            {c.author_id && <span className="ml-2 inline-flex align-middle"><AuthorThankTip userId={c.author_id} username={c.author_username} tipLinks={c.author_tip_links} thanksCount={c.author_thanks_count || 0} contextType="comment" contextId={c.id} /></span>}
                            {c.body && <div className="text-white"><RichText text={c.body} /></div>}
                            {c.gif && <img src={c.gif.full_url} alt="GIF" data-testid={`comment-gif-${c.id}`} className="mt-1 max-h-40 rounded-lg" />}
                        </div>
                        {user && user.id === c.author_id && (
                            <button type="button" onClick={() => remove(c.id)} className="text-[#666] hover:text-[#ff4500] shrink-0" title="Delete comment">
                                <Trash2 className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                ))
            )}
            {user ? (
                <MiniComposer onSubmit={submit} placeholder="Write a comment…" testidPrefix={`comment-composer-${postId}`} accent="#ff4500" showVideo={false} />
            ) : (
                <div className="text-xs text-[#a0a0a0] pt-1"><Link to="/login" className="text-[#ff4500] hover:underline">Log in</Link> to comment.</div>
            )}
        </div>
    );
}

function PostCard({ post, owner, onDelete }) {
    const [open, setOpen] = useState(false);
    return (
        <div id={`post-${post.id}`} data-testid={`board-post-${post.id}`} className="border border-[#2a2a2a] bg-[#141414] p-4">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">{new Date(post.created_at).toLocaleString()}</span>
                <ContextChip chip={post.chip} />
                {owner && owner.id && (
                    <AuthorThankTip userId={owner.id} username={owner.username} tipLinks={owner.tip_links} thanksCount={owner.thanks_count || 0} contextType="post" contextId={post.id} />
                )}
                {onDelete && (
                    <button type="button" onClick={onDelete} data-testid={`post-delete-${post.id}`} className="ml-auto text-[#a0a0a0] hover:text-red-400 transition-colors" title="Delete post">
                        <Trash2 className="h-4 w-4" />
                    </button>
                )}
            </div>
            {post.repost_of ? (
                <div className="mt-1 text-xs text-[#a0a0a0]" data-testid="repost-body">
                    <div className="flex items-center gap-1.5 text-[#22c55e]"><Repeat2 className="h-3.5 w-3.5" /> Reposted</div>
                    {post.body && <div className="text-sm text-white mt-1 whitespace-pre-line break-words" data-testid="repost-note">{post.body}</div>}
                    <a href={post.repost_of.link || "#"} className="mt-1 block border-l-2 border-[#2a2a2a] pl-3 hover:border-[#22c55e]">
                        <span className="text-[#ff4500] font-semibold">{post.repost_of.author_username}</span>
                        {(() => {
                            const firstLine = (post.repost_of.body || "").split("\n")[0].trim();
                            const subj = (post.repost_of.subject || "").replace(/…$/, "").trim();
                            return subj && !firstLine.startsWith(subj) ? <span className="text-white block">{post.repost_of.subject}</span> : null;
                        })()}
                        <span className="text-[#cfcfcf] block whitespace-pre-line break-words">{post.repost_of.body}</span>
                        <PostMedia images={post.repost_of.images} gif={post.repost_of.gif} videos={post.repost_of.videos} />
                    </a>
                </div>
            ) : (
                <>
                    {post.body && <div className="text-sm text-white mt-1"><RichText text={post.body} /></div>}
                    <PostMedia images={post.images} gif={post.gif} videos={post.videos} />
                    <PollView postType="wall" postId={post.id} poll={post.poll} accent="#ff4500" />
                </>
            )}
            <div className="mt-2 flex items-center gap-4 flex-wrap">
                <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`comments-toggle-${post.id}`}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#a0a0a0] hover:text-[#ff4500]">
                    <MessageSquare className="h-3.5 w-3.5" /> {post.comment_count || 0} Comment{post.comment_count === 1 ? "" : "s"}
                    <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                <ReactionBar post={post} accent="#ff4500" />
            </div>
            {!post.repost_of && (
                <PostActions
                    postType="wall"
                    postId={post.id}
                    link={post.link}
                    accent="#ff4500"
                    shareText={post.body}
                    initial={{ i_reposted: post.i_reposted, repost_count: post.repost_count, i_follow: post.i_follow, follow_count: post.follow_count }}
                />
            )}
            {open && <Comments postId={post.id} />}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Row renderers
// ---------------------------------------------------------------------------
function Thumb({ src, fallback, color }) {
    const [failed, setFailed] = useState(false);
    if (src && !failed) return <img src={src} alt="" onError={() => setFailed(true)} className="h-10 w-10 object-cover border border-[#2a2a2a] shrink-0 bg-white" />;
    return <span className="h-10 w-10 shrink-0 flex items-center justify-center border border-[#2a2a2a] bg-[#0f0f0f] font-display text-sm" style={{ color }}>{(fallback || "?").toUpperCase()}</span>;
}

function ListingRow({ item, color }) {
    // Sort grad years ascending and collapse consecutive runs into ranges (2030–2031).
    const years = [...new Set((item.grad_years || []).map(Number).filter(Boolean))].sort((a, b) => a - b);
    const yearChips = [];
    for (let i = 0; i < years.length;) {
        let j = i;
        while (j + 1 < years.length && years[j + 1] === years[j] + 1) j++;
        yearChips.push(j > i ? `${years[i]}–${years[j]}` : `${years[i]}`);
        i = j + 1;
    }
    const ageChips = [
        ...collapseRanges(item.age_groups || [], ALL_AGES),
        ...collapseRanges(item.grades || [], GRADES),
        ...yearChips,
    ].slice(0, 4);
    const g = item.genders || [];
    const gender = g.includes("M") && g.includes("F") ? "Boys · Girls" : g.includes("F") ? "Girls" : g.includes("M") ? "Boys" : "";
    const hasRating = (item.review_count || 0) > 0;
    const rc = item.roster_count || 0;
    const rosterSeg = item.kind === "team" && rc ? `${rc} roster${rc === 1 ? "" : "s"}` : null;
    // Flatten every detail into one dot-separated line so the row stays a single
    // compact line whenever the page is wide enough, wrapping only when it must.
    const metaSegs = [];
    const typeSport = item.type || item.sport;
    if (typeSport) metaSegs.push(typeSport);
    const loc = [item.city, item.state].filter(Boolean).join(", ");
    if (loc) metaSegs.push(loc);
    if (item.kind === "event" && item.event_date) metaSegs.push(fmt(item.event_date));
    if (rosterSeg) metaSegs.push(<span className="text-[#ff4500]">{rosterSeg}</span>);
    if (gender) metaSegs.push(gender);
    for (const c of ageChips) metaSegs.push(c);
    if (item.circuit) metaSegs.push(item.circuit);
    if (hasRating) metaSegs.push(<span className="text-[#fbbf24]">★ {Number(item.avg_rating).toFixed(1)} <span className="text-[#a0a0a0]">({item.review_count})</span></span>);
    return (
        <Link to={item.link} data-testid={`activity-${item.kind}-${item.id}`}
            style={{ borderLeft: `3px solid ${color}` }}
            className="group relative flex items-center gap-2.5 border border-[#2a2a2a] bg-[#141414] pl-3 pr-3 py-1.5 hover:border-[#ff4500] transition-colors">
            <span data-testid={`activity-listed-${item.id}`} style={{ color, borderColor: color }}
                className="absolute top-0 left-[7px] bg-black border text-[8px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-b-md">Listed</span>
            <Thumb src={item.logo || item.photo} fallback={item.name?.[0]} color={color} />
            <div className="min-w-0 flex-1 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5" data-testid={`activity-${item.kind}-meta-${item.id}`}>
                <span className="font-display text-base tracking-wide group-hover:text-[#ff4500]">{item.name}</span>
                {metaSegs.map((seg, i) => (
                    <span key={i} className="text-xs text-[#a0a0a0] inline-flex items-baseline gap-x-1.5">
                        <span className="text-[#3a3a3a]">·</span>{seg}
                    </span>
                ))}
            </div>
        </Link>
    );
}

function ReplyCard({ item, onDelete }) {
    return (
        <div data-testid={`activity-reply-${item.id}`} className="border border-[#2a2a2a] bg-[#141414] p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] flex items-center gap-2 flex-wrap">
                Replied · {new Date(item.created_at).toLocaleString()} <ContextChip chip={item.chip} />
                {onDelete && (
                    <button type="button" onClick={onDelete} data-testid={`reply-delete-${item.id}`} className="ml-auto text-[#a0a0a0] hover:text-red-400 transition-colors" title="Delete reply">
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
            <div className="text-sm text-white mt-1 whitespace-pre-line break-words">
                {item.subject ? <span className="text-[#8a8a8a]">Re: {item.subject} — </span> : null}{item.body}
            </div>
            {item.link && <Link to={item.link} className="text-xs text-[#ff4500] hover:underline mt-1 inline-block">View thread →</Link>}
        </div>
    );
}

function fmtEdited(iso, tz) {
    if (!iso) return "";
    const opts = { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" };
    try {
        return new Intl.DateTimeFormat("en-US", tz ? { ...opts, timeZone: tz } : opts).format(new Date(iso));
    } catch {
        return new Intl.DateTimeFormat("en-US", opts).format(new Date(iso));
    }
}

function ReviewCard({ item, isSelf, onDelete }) {
    const name = item.entity_name || item.team_name;
    return (
        <div data-testid={`activity-review-${item.id}`} className="border border-[#2a2a2a] bg-[#141414] p-4">
            <div className="flex items-center gap-2">
                <span className="text-[#fbbf24] text-sm">{"★".repeat(Math.round(item.rating || 0))}<span className="text-[#4a4a4a]">{"★".repeat(5 - Math.round(item.rating || 0))}</span></span>
                <span className="text-xs text-[#a0a0a0]">{item.rating?.toFixed ? item.rating.toFixed(1) : item.rating}</span>
                <span className="text-[10px] text-[#666] ml-auto">{fmt(item.created_at)}</span>
                {isSelf && (
                    <div className="flex items-center gap-2 pl-1">
                        <Link to={item.link} title="Edit your review" data-testid={`profile-review-edit-${item.id}`} className="text-[#a0a0a0] hover:text-[#ff4500] transition-colors">
                            <Pencil className="h-3.5 w-3.5" />
                        </Link>
                        <button type="button" title="Delete your review" data-testid={`profile-review-delete-${item.id}`} onClick={() => onDelete(item.id)} className="text-[#a0a0a0] hover:text-[#ff4500] transition-colors">
                            <Trash2 className="h-3.5 w-3.5" />
                        </button>
                    </div>
                )}
            </div>
            {item.title && <div className="text-sm font-semibold text-white mt-1.5">{item.title}</div>}
            {item.body && <div className="text-sm text-[#d0d0d0] mt-1 whitespace-pre-line break-words">{item.body}</div>}
            {Array.isArray(item.media) && item.media.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                    {item.media.map((m, i) => (
                        m.type === "video"
                            ? <video key={i} src={m.url} className="h-16 w-24 object-cover border border-[#2a2a2a] bg-black" muted />
                            : <img key={i} src={m.url} alt="" className="h-16 w-16 object-cover border border-[#2a2a2a]" />
                    ))}
                </div>
            )}
            {item.updated_at && item.updated_at !== item.created_at && (
                <div className="mt-2 text-[10px] text-[#666] italic" data-testid={`profile-review-edited-${item.id}`}>
                    Last edited {fmtEdited(item.updated_at, item.entity_timezone)}
                </div>
            )}
            {name && <Link to={item.link} className="text-xs text-[#ff4500] hover:underline mt-2 inline-block">on {name} →</Link>}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function UserBoard() {
    const { username } = useParams();
    const location = useLocation();
    const { user, isFollowing, toggleFollow, updateIdentity } = useAuth();
    const [summary, setSummary] = useState(null);
    const [rnPublic, setRnPublic] = useState(false);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [cache, setCache] = useState({});          // section -> items[]
    const [selected, setSelected] = useState([]);    // sections currently shown
    const [sortDesc, setSortDesc] = useState(() => localStorage.getItem("ub:sortDesc") !== "0"); // newest first
    const [secOpen, setSecOpen] = useState(false);   // section multiselect collapsed by default
    const [visibleCount, setVisibleCount] = useState(15); // feed pagination
    const [nameNote, setNameNote] = useState("");         // note popup when private is blocked

    useEffect(() => {
        let active = true;
        setLoading(true); setNotFound(false); setCache({}); setSelected([]);
        const qtab = new URLSearchParams(location.search).get("tab");
        api.get(`/users/by-username/${username}/profile`)
            .then((r) => {
                if (!active) return;
                setSummary(r.data);
                setRnPublic(!!r.data.real_name_public);
                // Default selection: a deep-linked ?tab= section, else the visitor's
                // saved preference, else every section.
                const secs = r.data.sections || [];
                let def = secs;
                if (qtab && secs.includes(qtab)) {
                    def = [qtab];
                } else {
                    try {
                        const saved = JSON.parse(localStorage.getItem("ub:sections") || "null");
                        if (Array.isArray(saved)) {
                            const filtered = saved.filter((s) => secs.includes(s));
                            if (filtered.length) def = filtered;
                        }
                    } catch { /* ignore bad pref */ }
                }
                setSelected(def);
            })
            .catch(() => { if (active) setNotFound(true); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [username]);

    const loadSection = useCallback(async (section) => {
        try {
            const { data } = await api.get(`/users/by-username/${username}/activity/${section}?limit=100`);
            setCache((c) => ({ ...c, [section]: data.items }));
        } catch {
            setCache((c) => ({ ...c, [section]: [] }));
        }
    }, [username]);

    // Reset how many feed items are shown when the view changes.
    useEffect(() => { setVisibleCount(15); }, [selected, sortDesc, username]);

    // Lazy-load any selected section we haven't fetched yet.
    useEffect(() => {
        selected.forEach((s) => { if (cache[s] === undefined) loadSection(s); });
    }, [selected, cache, loadSection]);

    // Merge all selected sections into one date-sorted feed.
    const feedItems = useMemo(() => {
        const arr = [];
        selected.forEach((s) => { if (Array.isArray(cache[s])) arr.push(...cache[s]); });
        arr.sort((a, b) => {
            const ap = a.pinned ? 1 : 0, bp = b.pinned ? 1 : 0;
            if (ap !== bp) return bp - ap;   // pinned posts float to the top
            const av = a.created_at || "", bv = b.created_at || "";
            return sortDesc ? bv.localeCompare(av) : av.localeCompare(bv);
        });
        return arr;
    }, [selected, cache, sortDesc]);
    const feedLoading = selected.some((s) => cache[s] === undefined);

    useHashScroll(!feedLoading && feedItems.length > 0);


    if (loading) return <div className="text-center text-[#a0a0a0] py-20">Loading…</div>;
    if (notFound || !summary) return <div className="text-center text-[#a0a0a0] py-20" data-testid="user-not-found">User not found.</div>;

    const profile = summary.profile;
    const following = isFollowing("user", profile.id);
    const isSelf = summary.is_self;

    const onFollow = async () => {
        const res = await toggleFollow("user", profile.id);
        if (res.ok) {
            setSummary((s) => ({ ...s, profile: { ...s.profile, follower_count: Math.max(0, (s.profile.follower_count || 0) + (res.following ? 1 : -1)) } }));
            toast.success(res.following ? "Following — manage alerts on your Following page" : "Unfollowed");
        } else toast.error(res.error);
    };

    const toggleRealName = async () => {
        const next = !rnPublic;
        try {
            const { data } = await api.put(`/me/profile-privacy`, { section: "real_name", public: next });
            setRnPublic(!!data.real_name_public);
            toast.success(`Your real name is now ${next ? "public" : "private"}`);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Update failed";
            // Going private but the account owns claimed listings → show an explanatory note.
            if (!next && err?.response?.status === 400) setNameNote(msg);
            else toast.error(msg);
        }
    };

    const changeTitle = async (t) => {
        const res = await updateIdentity({ pro_title: t });
        if (!res || res.ok === false) { toast.error(res?.error || "Could not update title"); return; }
        setSummary((s) => ({ ...s, profile: { ...s.profile, pro_title: t || null } }));
        toast.success(t ? `Title set to ${t}` : "Title cleared");
    };

    const deleteReview = async (rid) => {
        try {
            await api.delete(`/reviews/${rid}`);
            toast.success("Review deleted");
            setCache((c) => ({ ...c, reviews: Array.isArray(c.reviews) ? c.reviews.filter((x) => x.id !== rid) : c.reviews }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not delete review");
        }
    };

    const deletePost = async (item) => {
        if (!(await confirmAction({ title: "Delete post?", message: "This will permanently delete your post.", confirmText: "Delete" }))) return;
        try {
            const url = (item.post_type === "wall" || item.source === "wall") ? `/me/posts/${item.id}` : `/boards/posts/${item.id}`;
            await api.delete(url);
            toast.success("Post deleted");
            setCache((c) => {
                const nc = { ...c };
                for (const k of Object.keys(nc)) if (Array.isArray(nc[k])) nc[k] = nc[k].filter((x) => x.id !== item.id);
                return nc;
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not delete post");
        }
    };

    const editPost = async (item, newBody, media) => {
        const m = media || { images: item.images || [], videos: item.videos || [], gif: item.gif || null };
        try {
            const { data } = await api.patch(`/me/posts/${item.id}`, { body: newBody, images: m.images, videos: m.videos, gif: m.gif, clear_gif: !m.gif });
            toast.success("Post updated");
            setCache((c) => {
                const nc = { ...c };
                for (const k of Object.keys(nc)) if (Array.isArray(nc[k])) nc[k] = nc[k].map((x) => x.id === item.id ? { ...x, body: data.body, subject: (data.body || "").split("\n")[0], edited_at: data.edited_at, images: data.images, videos: data.videos, gif: data.gif } : x);
                return nc;
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update post");
            throw e;
        }
    };

    const pinPost = async (item) => {
        try {
            const { data } = await api.post(`/me/posts/${item.id}/pin`);
            toast.success(data.pinned ? "Pinned to the top of your wall" : "Unpinned");
            setCache((c) => {
                const nc = { ...c };
                for (const k of Object.keys(nc)) if (Array.isArray(nc[k])) nc[k] = nc[k].map((x) => (x.post_type === "wall" ? { ...x, pinned: x.id === item.id ? data.pinned : false } : x));
                return nc;
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not pin post");
        }
    };

    const deleteReplyItem = async (item) => {
        if (!(await confirmAction({ title: "Delete reply?", message: "This will permanently delete your reply.", confirmText: "Delete" }))) return;
        try {
            const url = item.reply_source === "entity" ? `/boards/replies/${item.id}` : `/comments/${item.id}`;
            await api.delete(url);
            toast.success("Reply deleted");
            setCache((c) => {
                const nc = { ...c };
                for (const k of Object.keys(nc)) if (Array.isArray(nc[k])) nc[k] = nc[k].filter((x) => x.id !== item.id);
                return nc;
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not delete reply");
        }
    };

    const count = Number(profile.thanks_count) || 0;
    const t = tierFor(count);

    const persistSections = (arr) => { try { localStorage.setItem("ub:sections", JSON.stringify(arr)); } catch { /* ignore */ } };
    const toggleSection = (s) => setSelected((cur) => {
        const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s];
        persistSections(next);
        return next;
    });
    const selectAll = () => { persistSections(summary.sections); setSelected(summary.sections); };
    const toggleSort = () => setSortDesc((v) => { const n = !v; try { localStorage.setItem("ub:sortDesc", n ? "1" : "0"); } catch { /* ignore */ } return n; });
    const sectionLabel = (s) => (s === "wall" ? `${profile.username}'s Wall` : SECTION_META[s].label);
    const ownerMeta = { id: profile.id, username: profile.username, tip_links: profile.tip_links, thanks_count: profile.thanks_count };

    const renderFeedItem = (item) => {
        const k = item.kind;
        // Reposts keep their dedicated card (shows the quoted original); everything else
        // uses the full feed post card (photos, color bar, listing header, action bar).
        if (k === "post" && item.repost_of) return <PostCard key={`wp-${item.id}`} post={item} owner={ownerMeta} onDelete={isSelf ? () => deletePost(item) : undefined} />;
        if (k === "post") return <FeedPostCard key={`p-${item.id}`} item={item} onDelete={isSelf ? deletePost : undefined} onEdit={isSelf && item.post_type === "wall" ? editPost : undefined} onPin={isSelf && item.post_type === "wall" ? pinPost : undefined} />;
        if (k === "reply") return <ReplyCard key={`rp-${item.id}`} item={item} onDelete={isSelf ? () => deleteReplyItem(item) : undefined} />;
        if (k === "review") return <ReviewCard key={`rv-${item.id}`} item={item} isSelf={isSelf} onDelete={deleteReview} />;
        const color = k === "event" ? "#22c55e" : k === "trainer" ? "#2196f3" : "#ff4500";
        return <ListingRow key={`${k}-${item.id}`} item={{ ...item, kind: k }} color={color} />;
    };

    return (
        <div className="w-full px-4 md:px-8 py-10 flex flex-col md:flex-row gap-6 md:gap-8">
            {nameNote && (
                <div className="fixed inset-0 z-[2000] bg-black/80 flex items-center justify-center p-4" data-testid="real-name-note-dialog" onClick={(e) => { if (e.target === e.currentTarget) setNameNote(""); }}>
                    <div className="w-full max-w-md bg-[#141414] border border-[#2a2a2a] p-6">
                        <div className="flex items-center gap-2 text-[#ff4500]">
                            <Lock className="h-5 w-5" />
                            <h3 className="font-display text-xl tracking-wide">Real name must stay public</h3>
                        </div>
                        <p className="text-sm text-[#cfcfcf] mt-3" data-testid="real-name-note-text">{nameNote}</p>
                        <div className="flex justify-end mt-6">
                            <button type="button" data-testid="real-name-note-ok" onClick={() => setNameNote("")} className="h-10 px-5 rounded-none bg-[#ff4500] hover:bg-[#e03e00] text-white uppercase tracking-[0.15em] text-xs font-semibold">Got it</button>
                        </div>
                    </div>
                </div>
            )}
            {/* Left rail: profile + section multiselect */}
            <aside className="md:w-64 shrink-0 md:sticky md:top-24 self-start w-full">
                <div className="flex items-center gap-3">
                    <TierPortal count={profile.thanks_count} size={56} ringW={4} gap={3} testid="board-owner-portal">
                        {profile.photo ? (
                            <img src={profile.photo} alt={profile.username} className="h-full w-full object-cover bg-white" />
                        ) : (
                            <div className="h-full w-full bg-[#1a1a1a] flex items-center justify-center text-lg font-display text-[#ff4500]">
                                {(profile.username || profile.name || "?").slice(0, 1).toUpperCase()}
                            </div>
                        )}
                    </TierPortal>
                    <div className="min-w-0">
                        <h1 className="font-display text-3xl tracking-wide leading-none truncate" data-testid="board-owner-username">{profile.username}</h1>
                        <div className="mt-1">
                            {isSelf ? (
                                <TitleSelect value={profile.pro_title || ""} isPro={isProUser(user)} onChange={changeTitle} />
                            ) : (
                                profile.pro_title ? (
                                    <span data-testid="board-owner-title" className="uppercase tracking-[0.15em] text-[11px] font-bold" style={{ color: titleColor(profile.pro_title, profile.is_pro) }}>
                                        {profile.pro_title}
                                    </span>
                                ) : null
                            )}
                        </div>
                    </div>
                </div>

                <div className="mt-3 text-xs text-[#a0a0a0] flex items-center gap-2 flex-wrap" data-testid="profile-thanks-panel">
                    <span data-testid="profile-panel-tier" className="inline-flex items-center gap-1.5 font-bold uppercase tracking-[0.12em]" style={{ color: t.color }}>
                        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: t.color }} />{t.name}
                    </span>
                    <span className="text-[#2a2a2a]">·</span>
                    <span><b className="text-white" data-testid="profile-thanks-count">{count.toLocaleString()}</b> thanks</span>
                    <span className="text-[#2a2a2a]">·</span>
                    <span><b className="text-white" data-testid="profile-follower-count">{(profile.follower_count || 0).toLocaleString()}</b> follower{profile.follower_count === 1 ? "" : "s"}</span>
                </div>

                {(profile.name || isSelf) && (
                    <div className="mt-2 text-sm truncate flex items-center gap-2" data-testid="profile-name-row">
                        <span data-testid="board-owner-name" className={isSelf ? (rnPublic ? "text-[#22c55e]" : "text-[#ef4444]") : "text-[#a0a0a0]"}>{profile.name || "—"}</span>
                        {isSelf && (
                            <button type="button" onClick={toggleRealName} data-testid="privacy-toggle-real_name" title="Who can see your real name"
                                className={`inline-flex items-center gap-1 text-[10px] tracking-[0.08em] hover:opacity-80 ${rnPublic ? "text-[#22c55e]" : "text-[#ef4444]"}`}>
                                {rnPublic ? <Globe className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                                {rnPublic ? "PUBLIC" : "PRIVATE"}
                            </button>
                        )}
                    </div>
                )}

                {!isSelf && user && (
                    <div className="mt-3 flex items-center gap-2">
                        <ThankButton toUser={profile.id} toName={profile.username} contextType="profile" contextId={profile.id} initialCount={profile.thanks_count} size="sm" />
                        <button type="button" onClick={onFollow} data-testid="board-follow-btn" title={following ? "Following" : "Follow"} aria-label={following ? "Following" : "Follow"}
                            className={`inline-flex items-center gap-1.5 h-9 px-3 uppercase tracking-wider text-xs font-semibold transition-colors ${following ? "bg-[#a855f7] text-white hover:bg-[#9333ea]" : "bg-[#0f0f0f] border border-[#a855f7] text-[#a855f7] hover:bg-[#a855f7]/10"}`}>
                            {following ? <BookmarkCheck className="h-4 w-4" /> : <BookmarkPlus className="h-4 w-4" />}
                            {following ? "Following" : "Follow"}
                        </button>
                        {following && (
                            <FollowAlertsBell targetType="user" targetId={profile.id} muteLabel={`Mute ${profile.username}`} size={18} align="start" />
                        )}
                    </div>
                )}

                {/* Section multiselect — every section is public; pick which to show.
                    Collapsible on mobile (collapsed by default); always expanded on desktop. */}
                <div className="mt-5 border-t border-[#2a2a2a] pt-3">
                    <button type="button" onClick={() => setSecOpen((v) => !v)} data-testid="sections-toggle"
                        className="w-full flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#a0a0a0] px-1 md:cursor-default">
                        <LayoutGrid className="h-3.5 w-3.5" /> <span>Show sections</span>
                        <span className="ml-auto text-[10px] normal-case tracking-normal text-[#666]">multi-select</span>
                        <ChevronDown className={`h-4 w-4 md:hidden transition-transform ${secOpen ? "rotate-180" : ""}`} />
                    </button>
                    <div className={`${secOpen ? "flex" : "hidden"} md:flex mt-2 flex-col`} data-testid="profile-sections">
                        {summary.sections.map((s) => {
                            const Icon = SECTION_META[s].icon;
                            const color = SECTION_COLOR[s];
                            const on = selected.includes(s);
                            return (
                                <button key={s} type="button" onClick={() => toggleSection(s)} data-testid={`section-toggle-${s}`}
                                    className={`flex items-center gap-2.5 pl-2 pr-2 py-2.5 border-l-2 text-left ${on ? "border-[#ff4500] bg-[#1a1a1a]" : "border-transparent hover:bg-[#161616]"}`}>
                                    <span className="h-4 w-4 shrink-0 border flex items-center justify-center" style={on ? { background: color, borderColor: color } : { borderColor: "#3a3a3a" }}>
                                        {on && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                    </span>
                                    <Icon className="h-3.5 w-3.5 shrink-0" style={{ color }} />
                                    <span className="flex-1 min-w-0 truncate text-xs uppercase tracking-[0.08em]" style={{ color: on ? color : "#a0a0a0" }}>{sectionLabel(s)}</span>
                                    <span className="text-[10px] text-[#666]">{summary.counts[s]}</span>
                                </button>
                            );
                        })}
                    </div>
                    <button type="button" onClick={selectAll} data-testid="section-select-all" className={`${secOpen ? "inline-block" : "hidden"} md:inline-block mt-1 px-2 text-[10px] uppercase tracking-[0.12em] text-[#ff4500] hover:opacity-80`}>Select all</button>
                </div>
            </aside>

            {/* Content: composer (self) + merged, date-sorted activity feed */}
            <main className="flex-1 min-w-0" data-testid="tab-overview">
                {isSelf && <FeedComposer onPosted={() => { setCache((c) => ({ ...c, wall: undefined })); }} />}

                <div className="flex items-center gap-3 mb-4">
                    <div className="text-[#ff4500] text-[11px] uppercase tracking-[0.25em]">Recent activity</div>
                    <button type="button" onClick={toggleSort} data-testid="feed-sort-dir"
                        className="ml-auto bg-[#141414] border border-[#2a2a2a] text-white text-xs px-3 py-1.5 uppercase tracking-[0.06em] hover:border-[#ff4500] transition-colors">
                        {sortDesc ? "▼ Newest" : "▲ Oldest"}
                    </button>
                </div>

                {selected.length === 0 ? (
                    <div className="border border-[#2a2a2a] py-14 text-center text-sm text-[#a0a0a0]" data-testid="feed-none-selected">Select a section on the left to see activity.</div>
                ) : feedLoading && feedItems.length === 0 ? (
                    <div className="text-sm text-[#a0a0a0] py-8 text-center">Loading…</div>
                ) : feedItems.length === 0 ? (
                    <div className="border border-[#2a2a2a] py-14 text-center text-sm text-[#a0a0a0]" data-testid="feed-empty">No activity yet.</div>
                ) : (
                    <>
                        <div className="space-y-1.5" data-testid="profile-feed">{feedItems.slice(0, visibleCount).map(renderFeedItem)}</div>
                        {feedItems.length > visibleCount && (
                            <button type="button" onClick={() => setVisibleCount((c) => c + 15)} data-testid="feed-load-more"
                                className="w-full mt-3 py-3 border border-[#2a2a2a] text-xs uppercase tracking-[0.12em] text-[#a0a0a0] hover:border-[#ff4500] hover:text-[#ff4500] transition-colors">
                                Load more ({feedItems.length - visibleCount} more)
                            </button>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}

